// Dashboard V-shape value types.
//
// `DashboardSpec` is the single new `Value` kind added to the engine
// (registered in `../../value.ts`'s union + `VALUE_KIND_TABLE`). The
// per-tile / per-sub-form descriptors below are NOT `Value` members —
// they are engine-internal tagged objects assembled by the dashboard
// scope's special-form ops (the same relationship `AggSpec` has to
// `aggregate`). Only `(dashboard …)` is callable at top level; it
// returns the lone `DashboardSpec` Value.
//
// V-shape: every column slot is an
// existing `ColumnRef` value — `ds` is a binding-name string or an
// inlined `LazyDataset`. No CDF traffic has happened when a
// `DashboardSpec` is produced; the renderer's per-tile resolution hook
// (4c.7) walks these `ColumnRef`s through the existing
// `resolveColumnRef` / materializer path on demand.
//
// `import type` only — keeps the runtime module graph acyclic
// (`../../value.ts` imports `DashboardSpec` from here at runtime for
// its `VALUE_KIND_TABLE` row; this file's reference to `ColumnRef` is
// erased).

import type { ColumnRef } from '../../value.ts';
import type {
  Align,
  AxisSide,
  Color,
  Curve,
  DisplayFormat,
  DonutLabels,
  MetricFormat,
  MetricVariant,
  Orientation,
  SeriesType,
  Stack,
  Stroke,
  XKind,
  XSpacing,
} from './enums.ts';

/**
 * Grid span `[width, height]` in 12-column units. Each component is an
 * integer in `[1, 12]` (validated by the tile ops, not here — this is
 * the carrier shape). Declaration order + span fully determines layout;
 * there is no per-tile position.
 */
export type Span = readonly [width: number, height: number];

/** The 12-column grid is fixed; `(dashboard …)` carries a `:title` kwarg but no layout kwargs. */
export const SPAN_RANGE = { min: 1, max: 12 } as const;

/**
 * One plotted series inside a `(cartesian …)` tile. `x` / `y` are
 * always explicit `(col …)` `ColumnRef`s (only the explicit form is
 * supported — no positional dataset / bare-symbol shape).
 */
export type SeriesSpec = {
  readonly kind: 'series-spec';
  readonly type: SeriesType;
  readonly id: string;
  readonly label: string;
  readonly color: Color;
  readonly axis: AxisSide;
  readonly curve: Curve;
  readonly x: ColumnRef;
  readonly y: ColumnRef;
  readonly stroke?: Stroke;
  /**
   * Optional third channel that sizes a `scatter` point (bubble radius).
   * Only meaningful when `type` is `'scatter'`; ignored otherwise. The
   * renderer wires it through a recharts `<ZAxis>` so the cell value maps
   * to point area.
   */
  readonly z?: ColumnRef;
};

/** A horizontal/vertical guide line inside a `(cartesian …)` tile. */
export type ReferenceLineSpec = {
  readonly kind: 'reference-line-spec';
  readonly axis: 'x' | 'y';
  /** Number, or an ISO/date string on a time x-axis (renderer normalizes). */
  readonly value: number | string;
  readonly label?: string;
  readonly style?: Stroke;
  readonly color?: Color;
};

/** A shaded band between two values inside a `(cartesian …)` tile. */
export type ReferenceAreaSpec = {
  readonly kind: 'reference-area-spec';
  readonly axis: 'x' | 'y';
  /** Numbers, or ISO/date strings on a time x-axis (renderer normalizes). */
  readonly start: number | string;
  readonly end: number | string;
  readonly label?: string;
  readonly color?: Color;
};

/**
 * A shaded min/max band inside a `(cartesian …)` tile. `lower` / `upper`
 * are explicit column refs plotted as a range area against the shared
 * `x` domain; independent of any series.
 */
export type BandSpec = {
  readonly kind: 'band-spec';
  readonly x: ColumnRef;
  readonly lower: ColumnRef;
  readonly upper: ColumnRef;
  readonly axis: AxisSide;
  readonly color?: Color;
  readonly label?: string;
};

/**
 * An x-axis configuration sub-form inside `(cartesian …)`. Carries the
 * axis label and optional spacing policy; folded into
 * `CartesianSpec.xLabel` / `CartesianSpec.xSpacing` by the tile op.
 */
export type XAxisSpec = {
  readonly kind: 'x-axis-spec';
  readonly label?: string;
  readonly spacing?: XSpacing;
};

/**
 * A y-axis configuration sub-form inside `(cartesian …)`. `side`
 * selects the left or right axis; folded into
 * `CartesianSpec.yLabelLeft` / `yLabelRight` by the tile op.
 */
export type YAxisSpec = {
  readonly kind: 'y-axis-spec';
  readonly side: AxisSide;
  readonly label?: string;
};

/**
 * A KPI metric tile. Only the `kpi` variant is supported.
 */
export type MetricSpec = {
  readonly kind: 'metric-spec';
  readonly variant: MetricVariant;
  readonly span: Span;
  readonly value: ColumnRef;
  readonly label: string;
  readonly format: MetricFormat;
  readonly delta?: ColumnRef;
  readonly asOf?: ColumnRef;
};

/** A cartesian (line/bar/area/scatter) chart tile. */
export type CartesianSpec = {
  readonly kind: 'cartesian-spec';
  readonly span: Span;
  readonly xKind: XKind;
  readonly stack: Stack;
  readonly orientation: Orientation;
  readonly series: readonly SeriesSpec[];
  readonly referenceLines: readonly ReferenceLineSpec[];
  readonly referenceAreas: readonly ReferenceAreaSpec[];
  readonly bands: readonly BandSpec[];
  /**
   * Resolved spacing policy for the x-axis. Set by `(x-axis :spacing …)`;
   * undefined means defer to the `'auto'` default — discrete iff any
   * bar series is present (so bars and ticks align), continuous
   * otherwise.
   */
  readonly xSpacing?: XSpacing;
  readonly xLabel?: string;
  readonly yLabelLeft?: string;
  readonly yLabelRight?: string;
};

/**
 * A category→color override map for `(donut …)` / `(treemap …)`. Keyed by
 * the raw category value (the resolved `:category` / `:label` cell as a
 * string); each value is a chart color token. Categories absent from the
 * map fall back to the shared order-stable assignment in the renderer.
 */
export type CategoryColors = Readonly<Record<string, Color>>;

/**
 * A donut (always inner-radius > 0) chart tile. One row per slice:
 * `:category` names the slice, `:value` sizes it. An optional center KPI
 * readout shows `:center` (or the auto-summed slice total when omitted but
 * `:center-label` is present).
 */
export type DonutSpec = {
  readonly kind: 'donut-spec';
  readonly span: Span;
  readonly category: ColumnRef;
  readonly value: ColumnRef;
  /** Number format for slice values and the center readout. */
  readonly format: DisplayFormat;
  /** Slice value labels: `percent` (default), raw `value`, or `none`. */
  readonly labels: DonutLabels;
  /** Whether to render the slice legend (default true). */
  readonly legend: boolean;
  /**
   * Optional scalar center value. When omitted but `centerLabel` is set,
   * the renderer auto-sums the slice values.
   */
  readonly center?: ColumnRef;
  /** Optional center KPI caption; its presence enables the center readout. */
  readonly centerLabel?: string;
  /** Optional category→color override map. */
  readonly colors?: CategoryColors;
  /** Optional top-N cap; slices past the top N collapse into one "Other" slice. */
  readonly maxSlices?: number;
};

/**
 * A treemap tile. One row per tile: `:label` names the tile, `:value`
 * sizes it. An optional `:group` adds one level of parent nesting.
 */
export type TreemapSpec = {
  readonly kind: 'treemap-spec';
  readonly span: Span;
  readonly label: ColumnRef;
  readonly value: ColumnRef;
  /** Optional one-level grouping column (parent rectangles). */
  readonly group?: ColumnRef;
  /** Optional category→color override map. */
  readonly colors?: CategoryColors;
  /** Optional top-N cap; tiles past the top N collapse into one "Other" tile. */
  readonly maxTiles?: number;
};

/** One column inside a `(table …)` tile. */
export type ColumnSpec = {
  readonly kind: 'column-spec';
  readonly ref: ColumnRef;
  readonly label: string;
  /** Display format; when omitted, derived from the column's type (enum → category badge, else text). */
  readonly format?: DisplayFormat;
  readonly align?: Align;
  readonly colorByValue?: boolean;
  readonly sort?: 'asc' | 'desc';
  readonly sortable?: boolean;
};

/** A tabular tile. */
export type TableSpec = {
  readonly kind: 'table-spec';
  readonly span: Span;
  readonly columns: readonly ColumnSpec[];
};

/** A free-text Markdown tile. Carries no data refs — just the source string. */
export type MarkdownSpec = {
  readonly kind: 'markdown-spec';
  readonly span: Span;
  readonly content: string;
};

/**
 * A renderable tile. Discriminated by `kind`; the renderer's
 * `useResolveTile` switches over it (4c.7).
 */
export type TileSpec = MetricSpec | CartesianSpec | TableSpec | DonutSpec | TreemapSpec | MarkdownSpec;
/**
 * Human-readable label for each top-level tile `kind`, keyed by the
 * `TileSpec` discriminant. Total over `TileSpec['kind']` (`satisfies`),
 * so adding or renaming a tile kind is a compile error until a label is
 * supplied. This is the single source of truth for the tile
 * presentation form — never re-derive it by string-munging the
 * discriminant (e.g. stripping the `-spec` suffix).
 */
export const TILE_KIND_LABELS = {
  'metric-spec': 'metric',
  'cartesian-spec': 'cartesian',
  'table-spec': 'table',
  'donut-spec': 'donut',
  'treemap-spec': 'treemap',
  'markdown-spec': 'markdown',
} satisfies Record<TileSpec['kind'], string>;

/**
 * The single dashboard `Value` kind. Holds a header `title` and the
 * tiles in declaration order; the renderer flows tiles left-to-right,
 * top-to-bottom across the fixed 12-column grid. Carries unresolved
 * `ColumnRef`s (V-shape) — no I/O has happened at construction time.
 *
 * A class (not a plain object) so `VALUE_KIND_TABLE`'s `instanceof`
 * discrimination in `../../value.ts` is unambiguous, mirroring
 * `ColumnRef` / `DescSpec`.
 */
export class DashboardSpec {
  readonly kind = 'dashboard-spec' as const;
  constructor(
    readonly title: string,
    readonly tiles: readonly TileSpec[],
  ) {}
}
